/**
 * Plan Audit — the fetch half. READ-ONLY: SELECTs through existing readers.
 *
 * PRD: docs/9-exploration/PRD-plan-audit.md. Wording: planAuditCore.ts.
 *
 * Reads, all existing:
 *   - getPlanVersionLineContents — what the version holds, split at its line
 *     (the same read as "What's in it" on the governance page);
 *   - the items themselves (owner, why now, desired outcome, duplicate state,
 *     category and level);
 *   - generateAlignmentReport — which of them link to no strategic objective,
 *     and which objectives (and goals under them) each one is linked to.
 * The relationship detection (computeViolations) runs in the hook, over the
 * relationship store every other caller of it reads.
 *
 * For the two Cascade checks (roadmap D-9), the cycle the plan was built from:
 *   - plan_cycle_governing_cycle — the same join authorize_por uses;
 *   - the cycle's name and strategy reference, its Catchball loops (and each
 *     request's strategic context), and its proposals in an accepted state;
 *   - the workspace's strategies, to find the one the cycle supports
 *     (strategyIdForCycle), and its objectives from the alignment report.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { getPlanVersionLineContents } from './planningService';
import { fetchHeldValues } from './itemCycleStandingService';
import type { ItemHeldValues } from './itemCycleStanding';
import { generateAlignmentReport, listStrategies, type AlignmentReport, type Strategy } from './strategyAlignment';
import { fetchPlanGoverningCycleId } from './planGoverningCycle';
import { strategyIdForCycle } from './strategy/cycleStrategy';
import type { WorkspaceStrategyKind } from './strategy/workspaceStrategies';
import { strategyIdOf } from '../features/item-detail-third-pass/useItemStrategy';
import { normalizeLoopIntent } from '../types/prioritization/catchballLoop';
import { PROPOSAL_ACCEPTED_STATUSES } from '../types/prioritization/catchballProposal';
import type { PlanAuditCycle, PlanAuditItem } from './planAuditCore';

export interface PlanAuditInputs {
  items: PlanAuditItem[];
  uncheckableCount: number;
  unlinkedItemIds: Set<string> | null;
  cycle: PlanAuditCycle | null;
  cycleUnreadReason: string | null;
  error: string | null;
}

const NO_CYCLE =
  'The Cascade checks did not run: this plan is not tied to a prioritization cycle (or its cycle could not be read), so there is no Cascade or stated intent to hold it against.';
const unreadCycle = (why: string) => `The Cascade checks did not run: the plan's cycle could not be read (${why}).`;

/** Another kind of strategy, as it reads inside a sentence. */
const OTHER_KIND_IN_SENTENCE: Record<Exclude<WorkspaceStrategyKind, 'classic'>, string> = {
  ost: 'opportunity tree',
  kernel: 'strategy kernel',
  canvas: 'Strategy Canvas',
  v2mom: 'V2MOM',
};

interface CycleStrategyRef {
  id: string | null;
  name: string;
  kind: WorkspaceStrategyKind | null;
  northStarGoal: string;
  description: string;
}

/** `degap_cycles.strategy_ref`, read defensively: it is JSON a person or an import wrote. */
function readStrategyRef(raw: unknown): CycleStrategyRef | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const kind = text(r.kind);
  return {
    id: text(r.id) || null,
    name: text(r.name),
    kind: kind in OTHER_KIND_IN_SENTENCE || kind === 'classic' ? (kind as WorkspaceStrategyKind) : null,
    northStarGoal: text(r.northStarGoal),
    description: text(r.description),
  };
}

interface CycleReads {
  cycle: { id: string; name: string; strategyRef: CycleStrategyRef | null } | null;
  loops: { intent: unknown }[];
  proposals: {
    id: string;
    item_id: string | null;
    aggregated_rank_list: string[] | null;
    aggregated_cut_line_position: number | null;
    objective_ids: string[] | null;
  }[];
  strategies: Strategy[];
  reason: string | null;
}

async function readCycle(planVersionId: string, workspaceId: string | null): Promise<CycleReads> {
  const none = (reason: string): CycleReads => ({ cycle: null, loops: [], proposals: [], strategies: [], reason });
  const db = getTypedSupabaseClient();
  const { data: version, error: versionError } = await db
    .from('plan_versions')
    .select('plan_cycle_id')
    .eq('id', planVersionId)
    .maybeSingle();
  if (versionError) return none(unreadCycle(versionError.message));
  if (!version) return none(NO_CYCLE);
  const cycleId = await fetchPlanGoverningCycleId(version.plan_cycle_id);
  if (!cycleId) return none(NO_CYCLE);

  const [cycleRes, loopsRes, proposalsRes, strategies] = await Promise.all([
    db.from('degap_cycles').select('id, name, strategy_ref').eq('id', cycleId).maybeSingle(),
    db.from('catchball_loops').select('id, intent').eq('cycle_id', cycleId),
    db
      .from('catchball_proposals')
      .select('id, item_id, aggregated_rank_list, aggregated_cut_line_position, objective_ids')
      .eq('cycle_id', cycleId)
      .in('status', PROPOSAL_ACCEPTED_STATUSES),
    workspaceId ? listStrategies(workspaceId) : Promise.resolve([] as Strategy[]),
  ]);
  const failed = cycleRes.error ?? loopsRes.error ?? proposalsRes.error;
  if (failed) return none(unreadCycle(failed.message));
  if (!cycleRes.data) return none(unreadCycle('it was not found'));

  return {
    cycle: {
      id: cycleRes.data.id,
      name: cycleRes.data.name?.trim() || 'This cycle',
      strategyRef: readStrategyRef(cycleRes.data.strategy_ref),
    },
    loops: loopsRes.data ?? [],
    proposals: proposalsRes.data ?? [],
    strategies,
    reason: null,
  };
}

/** The cycle, its Cascade and its stated intent, in the shape the checks read. */
function buildCycle(reads: CycleReads, report: AlignmentReport | null): PlanAuditCycle | null {
  if (!reads.cycle) return null;
  const { id, name, strategyRef: ref } = reads.cycle;
  const objectives = report?.objectives ?? [];
  const objectiveById = new Map(objectives.map((o) => [o.id, o]));

  const strategyId = strategyIdForCycle(id, ref, reads.strategies);
  const strategy = strategyId ? reads.strategies.find((s) => s.id === strategyId) ?? null : null;
  const intentObjectiveIds = new Set(
    strategy ? objectives.filter((o) => strategyIdOf(o, objectiveById) === strategy.id).map((o) => o.id) : [],
  );
  const otherStrategy =
    !strategy && ref?.kind && ref.kind !== 'classic' && ref.name
      ? { kindLabel: OTHER_KIND_IN_SENTENCE[ref.kind], name: ref.name }
      : null;
  const intentWords = [
    ...new Set(
      [
        !strategy && !otherStrategy ? ref?.name : '',
        ref?.northStarGoal,
        ref?.description,
        ...reads.loops.map((l) => normalizeLoopIntent(l.intent)?.strategicContext),
      ]
        .map((w) => (w ?? '').trim())
        .filter(Boolean),
    ),
  ];

  return {
    id,
    name,
    loopCount: reads.loops.length,
    acceptedProposals: reads.proposals.map((p) => ({
      id: p.id,
      itemId: p.item_id,
      rankList: p.aggregated_rank_list ?? [],
      cutLine: p.aggregated_cut_line_position,
      objectiveIds: p.objective_ids ?? [],
    })),
    strategy: strategy ? { id: strategy.id, name: strategy.name } : null,
    intentObjectiveIds,
    otherStrategy,
    intentWords,
    objectiveNames: new Map(objectives.map((o) => [o.id, o.name])),
  };
}

export async function loadPlanAuditInputs(
  planVersionId: string,
  workspaceId: string | null,
): Promise<PlanAuditInputs> {
  const empty: PlanAuditInputs = {
    items: [],
    uncheckableCount: 0,
    unlinkedItemIds: null,
    cycle: null,
    cycleUnreadReason: null,
    error: null,
  };
  const contents = await getPlanVersionLineContents(planVersionId);
  if (contents.error) return { ...empty, error: contents.error };

  const above = new Set(contents.above.map((e) => e.priorityId));
  const entryIds = [...contents.above, ...contents.below].map((e) => e.priorityId);
  if (entryIds.length === 0) return empty;

  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('items')
    .select('id, title, owner_id, why_now, desired_outcome, dedupe_status, duplicate_of_id, exit_status, item_category, altitude_level')
    .in('id', entryIds);
  if (error) return { ...empty, error: error.message };
  const rows = data ?? [];

  // An authorized plan is audited on what it holds, not on the live item (migration 1019). A
  // version still being prepared has no ranks of record and reads the item as it stands.
  const { data: authorized } = await db
    .from('item_official_ranks')
    .select('plan_cycle_id')
    .eq('plan_version_id', planVersionId)
    .limit(1)
    .maybeSingle();
  const held = authorized ? await fetchHeldValues(rows.map((r) => r.id), authorized.plan_cycle_id) : new Map<string, ItemHeldValues>();
  if (!held) return { ...empty, error: 'Could not read what the plan holds' };

  const [report, cycleReads] = await Promise.all([
    workspaceId && rows.length > 0
      ? generateAlignmentReport(
          workspaceId,
          rows.map((r) => r.id),
          new Map(rows.map((r) => [r.id, (held.get(r.id)?.title ?? r.title)?.trim() || 'Untitled item'])),
        )
      : Promise.resolve(null),
    readCycle(planVersionId, workspaceId),
  ]);

  const objectivesByItem = new Map<string, string[]>();
  for (const m of report?.mappings ?? []) {
    const list = objectivesByItem.get(m.priorityId) ?? [];
    if (!list.includes(m.objectiveId)) list.push(m.objectiveId);
    objectivesByItem.set(m.priorityId, list);
  }

  const items: PlanAuditItem[] = rows.map((r) => ({
    id: r.id,
    title: (held.get(r.id)?.title ?? r.title)?.trim() || 'Untitled item',
    aboveLine: above.has(r.id),
    ownerId: r.owner_id,
    whyNow: r.why_now,
    desiredOutcome: r.desired_outcome,
    dedupeStatus: r.dedupe_status,
    duplicateOfId: r.duplicate_of_id,
    exitStatus: r.exit_status,
    itemCategory: held.has(r.id) ? held.get(r.id)!.itemCategory : r.item_category,
    altitudeLevel: held.has(r.id) ? held.get(r.id)!.altitudeLevel : r.altitude_level,
    objectiveIds: objectivesByItem.get(r.id) ?? [],
  }));

  const unlinkedItemIds =
    report && report.totalObjectives > 0
      ? new Set(report.gaps.filter((g) => g.type === 'unmapped_priority').map((g) => g.entityId))
      : null;

  return {
    items,
    uncheckableCount: new Set(entryIds).size - items.length,
    unlinkedItemIds,
    cycle: buildCycle(cycleReads, report),
    cycleUnreadReason: cycleReads.reason,
    error: null,
  };
}
